import React, { useEffect, useRef, useState } from "react";
import { Facebook, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { brand, socialStrip, socialFeed } from "../data/site";

// Homepage project strip.
//
// By default this is a plain grid of Pepla's own work, rendered into the
// pre-built HTML so it is complete before any JavaScript runs. It has two
// optional upgrades, both off until Pepla has a Facebook page:
//
//   socialFeed.enabled      — swaps the tiles for the page's latest uploads,
//                             fetched through the Netlify function (needs a
//                             page token; see README > Facebook feed).
//   socialFeed.plugin       — adds Facebook's own embed panel beside the grid
//                             (needs no token, only brand.facebook).
//
// If either is on and Meta is unreachable, the grid below is what visitors
// keep seeing.
const ProjectStrip = () => {
  const [live, setLive] = useState(null);
  const [showPlugin, setShowPlugin] = useState(false);
  const [pluginFailed, setPluginFailed] = useState(false);
  const sectionRef = useRef(null);
  const loaded = useRef(false);

  const plugin = socialFeed.plugin || {};
  const pluginOn = Boolean(plugin.enabled && (plugin.pageUrl || brand.facebook));
  const pluginHeight = plugin.height || 640;
  const pluginSrc =
    `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(plugin.pageUrl || brand.facebook)}` +
    `&tabs=timeline&width=500&height=${pluginHeight}` +
    "&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=false";

  useEffect(() => {
    if (loaded.current) return undefined;
    if (!socialFeed.enabled && !pluginOn) return undefined;
    let alive = true;

    const load = () => {
      if (loaded.current) return;
      loaded.current = true;

      if (pluginOn) {
        // A blocked iframe still fires its load event while rendering an empty
        // white box, so probe Facebook first and only mount it if reachable.
        const stop = new AbortController();
        const giveUp = setTimeout(() => stop.abort(), 5000);
        fetch(pluginSrc, { mode: "no-cors", credentials: "omit", signal: stop.signal })
          .then(() => { if (alive) setShowPlugin(true); })
          .catch(() => { if (alive) setPluginFailed(true); })
          .finally(() => clearTimeout(giveUp));
      }

      if (!socialFeed.enabled) return;
      fetch(socialFeed.endpoint, { headers: { Accept: "application/json" } })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const items = Array.isArray(data?.items) ? data.items : [];
          if (alive && items.length >= socialFeed.count) setLive(items.slice(0, socialFeed.count));
        })
        .catch(() => {});
    };

    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      load();
      return () => { alive = false; };
    }
    const observer = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) { load(); observer.disconnect(); } },
      { rootMargin: "400px" }
    );
    observer.observe(node);
    return () => { alive = false; observer.disconnect(); };
  }, [pluginOn, pluginSrc]);

  const tiles = live || socialStrip.map((img, i) => ({
    id: `own-${i}`,
    image: img.src,
    link: null,
    alt: img.alt,
  }));

  // If a Facebook CDN image fails, fall back to the built-in photos.
  const handleError = () => { if (live) setLive(null); };

  // Nothing to show — no photos in socialStrip and no live feed. Render
  // nothing at all rather than an empty heading, the same way an empty
  // `testimonials` list removes the reviews section.
  if (!tiles.length && !pluginOn) return null;

  const gridCols = pluginOn ? "grid-cols-2 lg:grid-cols-3" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6";

  return (
    <section ref={sectionRef} className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <span className="kicker">{socialFeed.kicker}</span>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mt-2">See What We're Working On</h2>
          </div>
          {brand.facebook ? (
            <a
              href={brand.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-ink/20 hover:border-brand hover:text-brand font-semibold uppercase tracking-wider text-xs px-6 py-3 rounded-md transition-colors self-start sm:self-auto"
            >
              <Facebook size={16} /> Follow On Facebook
            </a>
          ) : (
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border border-ink/20 hover:border-brand hover:text-brand font-semibold uppercase tracking-wider text-xs px-6 py-3 rounded-md transition-colors self-start sm:self-auto"
            >
              Get A Free Inspection <ArrowRight size={16} />
            </Link>
          )}
        </div>

        <div className={pluginOn ? "grid lg:grid-cols-[1fr_360px] gap-8 items-start" : ""}>
          <div className={`grid ${gridCols} gap-3`}>
            {tiles.map((tile) => {
              const img = (
                <img
                  src={tile.image}
                  alt={tile.alt}
                  loading="lazy"
                  onError={handleError}
                  className="w-full h-full object-cover"
                />
              );
              return tile.link ? (
                <a key={tile.id} href={tile.link} target="_blank" rel="noopener noreferrer" className="img-zoom rounded-xl overflow-hidden aspect-square block">
                  {img}
                </a>
              ) : (
                <div key={tile.id} className="img-zoom rounded-xl overflow-hidden aspect-square">{img}</div>
              );
            })}
          </div>

          {pluginOn && (
            <div className="rounded-xl overflow-hidden border border-ink/10 bg-secondary" style={{ height: pluginHeight }}>
              {showPlugin && !pluginFailed ? (
                <iframe
                  title={`Latest posts from ${brand.name} on Facebook`}
                  src={pluginSrc}
                  height={pluginHeight}
                  style={{ border: "none", overflow: "hidden", width: "100%" }}
                  scrolling="no"
                  allow="encrypted-media; clipboard-write; picture-in-picture; web-share"
                  loading="lazy"
                />
              ) : (
                <div className="h-full flex items-center justify-center text-center px-6">
                  <p className="text-muted-foreground text-sm">
                    Our latest Facebook posts load here.{" "}
                    <a href={brand.facebook} target="_blank" rel="noopener noreferrer" className="text-brand-dark font-semibold hover:underline">
                      Open the page
                    </a>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProjectStrip;
